import { perform3dsCardLookup } from '@/features/shipment/services/shipment.services';
import { ThreeDsCardLookupDto, ThreeDsChallengeDto } from '@/features/shipment/types';
import { parseError } from '@/features/common/helpers/errorParser';

export function submit3dsForm(threeDsData: ThreeDsCardLookupDto): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.name = 'threeds-iframe';
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = threeDsData.threeDSecureMethodUrl || '';
    form.target = 'threeds-iframe';

    const textarea = document.createElement('textarea');
    textarea.name = 'threeDSMethodData';
    textarea.value = threeDsData.threeDSecureMethodData || '';
    form.appendChild(textarea);

    document.body.appendChild(form);

    let isResolved = false;
    const MAX_WAIT_TIME = 5000;
    const CLEANUP_DELAY = 100;

    const cleanup = (): void => {
      if (document.body.contains(form)) {
        document.body.removeChild(form);
      }
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    };

    const resolveOnce = (): void => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      resolve();
    };

    iframe.onload = (): void => {
      setTimeout(() => {
        resolveOnce();
      }, CLEANUP_DELAY);
    };

    const timeoutId = setTimeout(() => {
      resolveOnce();
    }, MAX_WAIT_TIME);

    try {
      form.submit();
    } catch (error) {
      clearTimeout(timeoutId);
      setTimeout(() => {
        resolveOnce();
      }, CLEANUP_DELAY);
    }
  });
}

export async function redirectTo3dsChallenge(challengeData: ThreeDsChallengeDto): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = challengeData.threeDSecureChallengeUrl;

  const textarea = document.createElement('textarea');
  textarea.name = 'creq';
  textarea.value = challengeData.threeDSecureChallengeData;
  form.appendChild(textarea);

  document.body.appendChild(form);
  form.submit();
}

export class CardLookupError extends Error {
  public errorMessage: string;

  constructor(message: string) {
    super(message);
    this.name = 'CardLookupError';
    this.errorMessage = message;
  }
}

export async function handle3dsCardLookup(): Promise<ThreeDsCardLookupDto | null> {
  try {
    const threeDsData = await perform3dsCardLookup();

    if (!threeDsData) {
      return null;
    }

    if (!threeDsData.cardLookupId) {
      throw new CardLookupError(
        'Due to a recent system update, we need you to re-enter your card details to complete this transaction.',
      );
    }

    if (!threeDsData.threeDSecureVersion
      || !threeDsData.threeDSecureMethodUrl || !threeDsData.threeDSecureMethodData) {
      throw new CardLookupError(
        'We\'re unable to verify this card for 3D Secure authentication. Please try a different card.',
      );
    }

    await submit3dsForm(threeDsData);
    return threeDsData;
  } catch (error) {
    if (error instanceof CardLookupError) {
      throw error;
    }

    // Parse the error to extract the error message from the backend
    const parsedError = parseError(error);
    throw new CardLookupError(parsedError.errorMessage);
  }
}
